import React from 'react';
import type { StandardFeatures, VehicleFeatures, VehicleSpecifications } from '@makemydeal/dr-platform-types';
import {
    SpecLabel,
    SpecValue,
    SubSectionBlock,
    SubSectionHeader,
    SpecGrid,
    FeatureList,
    FeatureListItem,
    FeatureItemLabel
} from './StandardFeaturesSlideout.style';
import { SPEC_SECTION_KEYS, VEHICLE_CONDITION_NEW } from '../../constants';
import type { SectionVisibility, SpecField, SpecSectionConfig, SpecSections } from '../../types/StandardFeaturesSlideout';

export const FEATURE_SECTIONS_LEFT: { key: keyof VehicleFeatures; label: string }[] = [
    { key: 'comfortAndConvenience', label: 'Comfort & Convenience' },
    { key: 'interior', label: 'Interior' },
    { key: 'exterior', label: 'Exterior' }
];

export const FEATURE_SECTIONS_RIGHT: { key: keyof VehicleFeatures; label: string }[] = [
    { key: 'mechanical', label: 'Mechanical' },
    { key: 'technology', label: 'Technology' },
    { key: 'safetyAndDriverAssist', label: 'Safety & Driver Assist' },
    { key: 'package', label: 'Package' }
];

/** A value is displayable when it is neither nullish nor an empty string (0 is displayable). */
export const hasValue = (value: unknown): boolean => value != null && value !== '';

const hasAnyValue = (source?: Record<string, unknown> | null): boolean => !!source && Object.values(source).some(hasValue);

export const renderSpecRow = (label: string, value: string | number | null | undefined) => {
    if (!hasValue(value)) return null;
    return (
        <React.Fragment key={label}>
            <SpecLabel size="small" weight="regular" forwardedAs="span">
                {label}:
            </SpecLabel>
            <SpecValue size="small" weight="regular" forwardedAs="span">
                {value}
            </SpecValue>
        </React.Fragment>
    );
};

export const renderSpecSection = ({ key, title, fields }: SpecSectionConfig) => {
    if (!fields.length) return null;
    return (
        <SubSectionBlock key={key}>
            <SubSectionHeader level={6} weight="bold" forwardedAs="h6">
                {title}
            </SubSectionHeader>
            <SpecGrid>{fields.map(([label, value]) => renderSpecRow(label, value))}</SpecGrid>
        </SubSectionBlock>
    );
};

/** Keeps only the fields with a displayable value, dropping the section entirely when none remain. */
const buildSpecSection = (key: SpecSectionConfig['key'], title: string, fields: SpecField[]): SpecSectionConfig | null => {
    const populatedFields = fields.filter(([, value]) => hasValue(value));
    return populatedFields.length ? { key, title, fields: populatedFields } : null;
};

export const renderFeatureColumn = (sections: { key: keyof VehicleFeatures; label: string }[], features: VehicleFeatures) =>
    sections.map(({ key, label }) => {
        const items = features[key];
        if (!items?.length) return null;
        return (
            <SubSectionBlock key={key}>
                <SubSectionHeader level={6} weight="bold" forwardedAs="h6">
                    {label}
                </SubSectionHeader>
                <FeatureList>
                    {items.map((item) => (
                        <FeatureListItem key={item}>
                            <FeatureItemLabel size="small" weight="regular" forwardedAs="span">
                                {item}
                            </FeatureItemLabel>
                        </FeatureListItem>
                    ))}
                </FeatureList>
            </SubSectionBlock>
        );
    });

export const buildLeftSpecSections = (specs: VehicleSpecifications): SpecSectionConfig[] =>
    [
        specs.engine &&
            buildSpecSection(SPEC_SECTION_KEYS.ENGINE, 'Engine', [
                ['Engine', specs.engine.engine],
                ['Displacement', specs.engine.displacement],
                ['Cylinders', specs.engine.cylinders],
                ['Valves', specs.engine.valves],
                ['Block Type', specs.engine.blockType],
                ['Camshaft', specs.engine.camshaft],
                ['Aspiration', specs.engine.aspiration],
                ['Fuel Type', specs.engine.fuelType],
                ['Fuel Intake', specs.engine.fuelIntake],
                ['Horsepower', specs.engine.horsepower],
                ['Electric Motors', specs.engine.electricMotors],
                ['Motor', specs.engine.motor]
            ]),
        specs.transmission &&
            buildSpecSection(SPEC_SECTION_KEYS.TRANSMISSION, 'Transmission', [
                ['Transmission', specs.transmission.transmission],
                ['Type', specs.transmission.type],
                ['Gears', specs.transmission.gears]
            ]),
        specs.drivetrain &&
            buildSpecSection(SPEC_SECTION_KEYS.DRIVETRAIN, 'Drivetrain', [
                ['Drivetrain', specs.drivetrain.drivetrain],
                ['Type', specs.drivetrain.type],
                ['Rear Axle Ratio', specs.drivetrain.rearAxleRatio]
            ])
    ].filter(Boolean) as SpecSectionConfig[];

export const buildRightSpecSections = (specs: VehicleSpecifications): SpecSectionConfig[] =>
    [
        specs.body &&
            buildSpecSection(SPEC_SECTION_KEYS.BODY, 'Body', [
                ['Body Style', specs.body.bodyStyle],
                ['Vehicle Type', specs.body.vehicleType],
                ['Segment', specs.body.segment],
                ['Cab Type', specs.body.cabType],
                ['Bed Length', specs.body.bedLength],
                ['Box Length', specs.body.boxLength],
                ['Passenger Doors', specs.body.passengerDoors]
            ]),
        specs.weightsAndCapacities &&
            buildSpecSection(SPEC_SECTION_KEYS.WEIGHTS_AND_CAPACITIES, 'Weights & capacities', [
                ['Gross Vehicle Weight', specs.weightsAndCapacities.grossVehicleWeight],
                ['Gross Combined Weight', specs.weightsAndCapacities.grossCombinedWeight],
                ['Front Axle', specs.weightsAndCapacities.frontAxle],
                ['Rear Axle', specs.weightsAndCapacities.rearAxle],
                ['Seating Capacity', specs.weightsAndCapacities.seatingCapacity],
                ['Fuel Tank Capacity', specs.weightsAndCapacities.fuelTankCapacity]
            ]),
        specs.fuelEconomy &&
            buildSpecSection(SPEC_SECTION_KEYS.FUEL_ECONOMY, 'Fuel economy', [
                ['City', specs.fuelEconomy.city],
                ['Highway', specs.fuelEconomy.highway],
                ['Combined', specs.fuelEconomy.combined],
                ['City (MPGe)', specs.fuelEconomy.cityMpge],
                ['Highway (MPGe)', specs.fuelEconomy.highwayMpge],
                ['Combined (MPGe)', specs.fuelEconomy.combinedMpge],
                ['Electric Range', specs.fuelEconomy.electricRange],
                ['Battery', specs.fuelEconomy.battery],
                ['Battery (usable)', specs.fuelEconomy.batteryUsable],
                ['Efficiency', specs.fuelEconomy.efficiency],
                ['Connector', specs.fuelEconomy.connector],
                ['Max Charging', specs.fuelEconomy.maxCharging],
                ['Charge Time (240V)', specs.fuelEconomy.chargeTime240V]
            ])
    ].filter(Boolean) as SpecSectionConfig[];

export const WARRANTY_LABELS: [contentKey: string, label: string][] = [
    ['Basic', 'Comprehensive'],
    ['Drivetrain', 'Powertrain'],
    ['Corrosion', 'Anti-Corrosion'],
    ['Rust-Through', 'Corrosion (rust-through)'],
    ['Roadside Assistance', 'Roadside Assistance'],
    ['Maintenance', 'Maintenance'],
    ['Traction Battery', 'Traction Battery'],
    ['Hybrid/Electric Components', 'Hybrid/Electric Components']
];

const WARRANTY_LABEL_KEYS = new Set(WARRANTY_LABELS.map(([contentKey]) => contentKey));

export const buildWarrantyFields = (warranty: Record<string, string>): SpecField[] => {
    const labelled = WARRANTY_LABELS.filter(([contentKey]) => hasValue(warranty[contentKey])).map(
        ([contentKey, label]): SpecField => [label, warranty[contentKey]]
    );
    const unrecognized = Object.entries(warranty)
        .filter(([contentKey, value]) => !WARRANTY_LABEL_KEYS.has(contentKey) && hasValue(value))
        .map(([contentKey, value]): SpecField => [contentKey, value]);

    return [...labelled, ...unrecognized];
};

/** True when at least one exterior/interior color name is present. */
export const hasColorsContent = (colors?: VehicleSpecifications['colors']): boolean =>
    hasValue(colors?.exteriorOemName) || hasValue(colors?.interiorOemName);

export const isNewVehicle = (condition?: string): boolean => condition?.toLowerCase() === VEHICLE_CONDITION_NEW;

/**
 * Used vehicles' warranty coverage is calculated from in-service date and odometer,
 * so without both we can't show accurate warranty content for them.
 */
export const hasWarrantyContent = (
    warranty: VehicleSpecifications['warranty'],
    condition?: string,
    inServiceDate?: string,
    odometer?: number
): boolean =>
    hasAnyValue(warranty as Record<string, unknown> | undefined) &&
    (isNewVehicle(condition) || (hasValue(inServiceDate) && hasValue(odometer)));

export const hasSpecificationsContent = (
    specs: VehicleSpecifications | undefined,
    condition?: string,
    inServiceDate?: string,
    odometer?: number
): boolean =>
    !!specs &&
    (buildLeftSpecSections(specs).length > 0 ||
        buildRightSpecSections(specs).length > 0 ||
        hasColorsContent(specs.colors) ||
        hasWarrantyContent(specs.warranty, condition, inServiceDate, odometer));

export const hasFeaturesContent = (features?: VehicleFeatures): boolean =>
    !!features && Object.values(features).some((items) => Array.isArray(items) && items.length > 0);

/** Builds both spec columns, yielding empty columns when there are no specifications at all. */
export const getSpecSections = (specs?: VehicleSpecifications): SpecSections => ({
    leftSpecSections: specs ? buildLeftSpecSections(specs) : [],
    rightSpecSections: specs ? buildRightSpecSections(specs) : []
});

/** A section object can arrive with every field empty — only show sections that have something to display. */
export const getSectionVisibility = (
    { specifications, features, dimensions }: StandardFeatures = {},
    condition?: string,
    inServiceDate?: string,
    odometer?: number
): SectionVisibility => {
    const showSpecs = hasSpecificationsContent(specifications, condition, inServiceDate, odometer);
    const showFeatures = hasFeaturesContent(features);
    const showDimensions =
        hasValue(dimensions?.width) ||
        hasValue(dimensions?.height) ||
        hasValue(dimensions?.length) ||
        hasValue(dimensions?.wheelbase);

    return {
        showSpecs,
        showFeatures,
        showDimensions,
        showEmptyState: !showSpecs && !showFeatures && !showDimensions
    };
};
